import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Check, ExternalLink, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  ApiError,
  createPromptEnhancementQuote,
  runPromptEnhancement,
  updatePromptRevision,
  type PromptRevisionInfo,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatUsd } from "@/lib/format";

interface EnhancerInput {
  modelKey: string;
  capabilityKey: string;
  prompt: string;
  params: Record<string, unknown>;
  changeKey?: string;
  projectId: string | null;
  onApply: (prompt: string) => void;
}

export interface PromptEnhancerController {
  result: PromptRevisionInfo | null;
  draft: string;
  loading: "enhance" | "apply" | null;
  error: string | null;
  isApplied: boolean;
  setDraft: (value: string) => void;
  /** Quote, balance-check and run the enhancement as one explicit UI action. */
  enhance: () => Promise<boolean>;
  apply: () => Promise<boolean>;
  discard: () => void;
}

const usd = (value: number) => formatUsd(value) ?? "—";

const mediaCount = (value: unknown) => {
  if (Array.isArray(value)) return value.length;
  return value ? 1 : 0;
};

const referenceAssetIds = (value: unknown): string[] =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) => (item && typeof item === "object" ? (item as { asset_id?: unknown }).asset_id : null))
    .filter((id): id is string => typeof id === "string")
    .slice(0, 16);

const studioContextFor = (params: Record<string, unknown>, modelKey: string) => {
  const context: {
    duration?: number;
    ratio?: string;
    resolution?: string;
    size?: string;
    generate_audio?: boolean;
    camera?: string;
    creative_direction?: Record<string, unknown>;
    reference_images: number;
    reference_videos: number;
    reference_audios: number;
    has_first_frame: boolean;
    has_last_frame: boolean;
    reference_asset_ids?: string[];
  } = {
    reference_images: mediaCount(params.reference_images),
    reference_videos: mediaCount(params.source_video) + mediaCount(params.reference_videos),
    reference_audios: mediaCount(params.reference_audios),
    has_first_frame: mediaCount(params.image) > 0,
    has_last_frame: mediaCount(params.end_image) > 0,
  };
  const duration = Number(params.duration);
  if (Number.isInteger(duration)) context.duration = duration;
  for (const key of ["ratio", "resolution", "size"] as const) {
    if (typeof params[key] === "string" && params[key]) context[key] = params[key];
  }
  if (typeof params.generate_audio === "boolean") context.generate_audio = params.generate_audio;
  if (typeof params.camera === "string" && params.camera.trim()) context.camera = params.camera.trim();
  if (params.creative_direction && typeof params.creative_direction === "object" && !Array.isArray(params.creative_direction)) {
    context.creative_direction = params.creative_direction as Record<string, unknown>;
  }
  // GPT Image 2.5 edits carry their references under `image`; the rewriter
  // looks at them to name what must not carry over into the output.
  if (modelKey.startsWith("gpt-image-2.5-")) {
    context.reference_images = mediaCount(params.image);
    context.has_first_frame = false;
    const ids = referenceAssetIds(params.image);
    if (ids.length) context.reference_asset_ids = ids;
  }
  return context;
};

// Shared hook lives beside its tightly-coupled result UI so both Studio surfaces
// consume exactly the same revision state.
// eslint-disable-next-line react-refresh/only-export-components
export function usePromptEnhancer({
  modelKey,
  capabilityKey,
  prompt,
  params,
  changeKey = "",
  projectId,
  onApply,
}: EnhancerInput): PromptEnhancerController {
  const [result, setResult] = useState<PromptRevisionInfo | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState<PromptEnhancerController["loading"]>(null);
  const [error, setError] = useState<string | null>(null);
  const [isApplied, setIsApplied] = useState(false);
  const studioContext = useMemo(() => studioContextFor(params, modelKey), [params, modelKey]);
  const studioContextKey = JSON.stringify(studioContext);
  const signatureFor = useCallback(
    (promptValue: string) => JSON.stringify([modelKey, capabilityKey, promptValue, projectId, studioContextKey, changeKey]),
    [modelKey, capabilityKey, projectId, studioContextKey, changeKey]
  );
  const inputSignature = signatureFor(prompt);
  const inputSignatureRef = useRef(inputSignature);
  inputSignatureRef.current = inputSignature;
  const previousSignature = useRef(inputSignature);
  const expectedAppliedSignature = useRef<string | null>(null);

  const discard = useCallback(() => {
    expectedAppliedSignature.current = null;
    setResult(null);
    setDraft("");
    setError(null);
    setIsApplied(false);
  }, []);

  useEffect(() => {
    if (previousSignature.current === inputSignature) return;
    previousSignature.current = inputSignature;
    if (expectedAppliedSignature.current === inputSignature) {
      expectedAppliedSignature.current = null;
      return;
    }
    discard();
  }, [discard, inputSignature]);

  /**
   * The Enhance click is the explicit paid action. Keep the backend's immutable
   * quote, balance check and settlement contract, but submit the accepted quote
   * immediately instead of interrupting this low-cost helper with a second
   * generation-style confirmation dialog.
   */
  const enhance = useCallback(async () => {
    if (prompt.trim().length < 3) {
      setError("Write a prompt before enhancing it.");
      return false;
    }
    setLoading("enhance");
    setError(null);
    setIsApplied(false);
    const signatureAtRun = inputSignatureRef.current;
    try {
      const quote = await createPromptEnhancementQuote({
        model_key: modelKey,
        capability_key: capabilityKey,
        prompt,
        project_id: projectId ?? undefined,
        studio_context: studioContext,
      });
      if (!quote.wallet.sufficient) {
        setError("Insufficient USD balance or member spending limit for this prompt enhancement.");
        return false;
      }
      const revision = await runPromptEnhancement(quote.id);
      // The composer is editable while this is in flight. If the prompt moved
      // on, the result belongs to text the user has abandoned — applying it
      // would silently overwrite what they typed and bill a generation from
      // the wrong source.
      if (signatureAtRun !== inputSignatureRef.current) {
        setError("Your prompt or settings changed while the enhancement was running. Run it again.");
        return false;
      }
      setResult(revision);
      setDraft(revision.enhanced_prompt);
      return true;
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Prompt enhancement failed");
      return false;
    } finally {
      setLoading(null);
    }
  }, [capabilityKey, modelKey, projectId, prompt, studioContext]);

  const updateDraft = useCallback((value: string) => {
    setDraft(value);
    setIsApplied(false);
  }, []);

  const apply = useCallback(async () => {
    if (!result || draft.trim().length < 3) return false;
    setLoading("apply");
    setError(null);
    try {
      const saved = await updatePromptRevision(result.id, {
        status: "applied",
        enhanced_prompt: draft,
      });
      setResult(saved);
      expectedAppliedSignature.current = signatureFor(draft);
      setIsApplied(true);
      onApply(draft);
      return true;
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the prompt revision");
      return false;
    } finally {
      setLoading(null);
    }
  }, [draft, onApply, result, signatureFor]);

  return {
    result,
    draft,
    loading,
    error,
    isApplied,
    setDraft: updateDraft,
    enhance,
    apply,
    discard,
  };
}

export function PromptEnhancerToggle({
  id,
  checked,
  disabled,
  onCheckedChange,
  compact = false,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
  compact?: boolean;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-3", compact ? "min-w-fit" : "rounded-xl bg-studio-accent-soft/60 px-3 py-2") }>
      <label htmlFor={id} className="min-w-0 cursor-pointer">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-studio-ink">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-studio-accent" /> Improve prompt first
        </span>
        {!compact && <span className="mt-0.5 block text-[10px] leading-4 text-studio-muted">Create and review an improved prompt before generation.</span>}
      </label>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export function PromptEnhancerResult({ controller, className, idPrefix = "studio" }: { controller: PromptEnhancerController; className?: string; idPrefix?: string }) {
  const { result, draft, loading, error, isApplied, setDraft, apply, discard } = controller;
  if (!result) return null;
  const snapshotFallback = result.citations.some((citation) => citation.fallback);

  return (
    <section className={cn("mt-5 rounded-2xl border border-studio-accent/25 bg-studio-accent-soft/60 p-4 shadow-sm", className)} aria-label="Prompt enhancement result" aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-xs font-semibold text-studio-ink">
            <Sparkles className="h-4 w-4 text-studio-accent" /> Improved prompt
            {isApplied && <span className="rounded-full bg-studio-success/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-studio-success">In composer</span>}
          </p>
          <p className="mt-1 text-pretty text-[11px] leading-4 text-studio-muted">
            Review or edit it, then use it in the composer. Enhancement cost: <span className="font-mono tabular-nums text-studio-ink">{result.cost_usd != null ? usd(result.cost_usd) : "Usage verification pending"}</span>
          </p>
          {result.cost_usd == null && <p className="mt-1 text-[11px] text-studio-muted">Your prompt is ready to use. The approved amount remains reserved until usage is verified.</p>}
        </div>
        <button type="button" onClick={discard} aria-label="Discard improved prompt" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-studio-muted transition-[color,background-color,transform] hover:bg-studio-card hover:text-studio-ink active:scale-[0.96] sm:h-10 sm:w-10">
          <X className="h-4 w-4" />
        </button>
      </div>

      <label htmlFor={`${idPrefix}-enhanced-prompt-${result.id}`} className="mt-4 block text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Prompt · editable</label>
      <textarea
        id={`${idPrefix}-enhanced-prompt-${result.id}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={8}
        disabled={loading === "apply"}
        className="mt-1 w-full resize-y rounded-xl border border-studio-border bg-studio-panel px-3 py-2.5 text-xs leading-5 text-studio-ink outline-none transition-[border-color,box-shadow] focus:border-studio-accent/60 focus:shadow-glow-sm disabled:opacity-70"
      />

      {result.warnings.map((warning) => <p key={warning} className="mt-2 text-[11px] leading-4 text-studio-warning">{warning}</p>)}

      {snapshotFallback && (
        <p role="status" className="mt-3 rounded-lg border border-studio-warning/25 bg-studio-warning/10 px-3 py-2 text-[11px] leading-4 text-studio-warning">
          Live provider guidance was unavailable. This revision used the versioned repository snapshot.
        </p>
      )}

      <details className="mt-3 rounded-xl bg-studio-panel/65 px-3 text-[11px] text-studio-muted">
        <summary className="min-h-10 cursor-pointer py-3 font-semibold text-studio-ink marker:text-studio-accent">Original, suggestions &amp; sources</summary>
        <div className="space-y-4 border-t border-studio-border pb-3 pt-3">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Original prompt</p>
            <p className="mt-1 max-h-24 overflow-y-auto text-pretty leading-5">{result.original_prompt}</p>
          </div>
          {result.suggestions.length > 0 && (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Optional suggestions</p>
              <ul className="mt-1.5 space-y-1.5">
                {result.suggestions.map((suggestion) => <li key={suggestion} className="text-pretty leading-4">• {suggestion}</li>)}
              </ul>
            </div>
          )}
          {result.applied_guidance.length > 0 && (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">What was improved</p>
              <ul className="mt-1.5 space-y-1.5">
                {result.applied_guidance.map((item) => <li key={item} className="text-pretty leading-4">• {item}</li>)}
              </ul>
            </div>
          )}
          {result.citations.length > 0 && (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Sources · {result.citations.length}</p>
              <ul className="mt-1.5 space-y-1.5">
                {result.citations.map((citation) => (
                  <li key={`${citation.path}:${citation.content_hash}`} className="flex min-w-0 items-center gap-1.5">
                    <BookOpen className="h-3 w-3 shrink-0 text-studio-accent" />
                    {citation.url ? (
                      <a href={citation.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-1 font-medium text-studio-ink hover:text-studio-accent">
                        <span className="truncate">{citation.heading}</span><ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : <span className="truncate font-medium text-studio-ink">{citation.heading}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>

      <div className="mt-4 flex justify-end">
        {isApplied ? (
          <p role="status" className="flex min-h-10 items-center gap-2 text-xs font-semibold text-studio-success"><Check className="h-4 w-4" /> This prompt is in the composer</p>
        ) : (
          <Button className="w-full active:scale-[0.96] sm:w-auto" type="button" size="sm" loading={loading === "apply"} onClick={() => void apply()}>
            {loading !== "apply" && <Check className="h-3.5 w-3.5" />} Use this prompt
          </Button>
        )}
      </div>
      {error && <p role="alert" className="mt-3 text-xs text-studio-danger">{error}</p>}
    </section>
  );
}
